import classNames from 'classnames'
import dayjs from 'dayjs'
import {t} from 'i18next'
import {Calendar} from 'primereact/calendar'
import {Dropdown} from 'primereact/dropdown'
import {Fieldset} from 'primereact/fieldset'
import {InputText} from 'primereact/inputtext'
import React, {Fragment} from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import {PostBox} from './PostBox'
import {SalutationCt, UserAccountType} from '../../../../model/enums/enums'
import {Person} from '../../../../model/person.model'
import {SalutationElement} from '../../../../util/salutation'
import {getEmailErrorMessage, getFormErrorMessage, getRequiredFormMessage} from '../../../util/ErrorText'
import {
    getBirthDayYearRange,
    getMonthNavigatorTemplate,
    getYearNavigatorTemplate
} from '../../../util/form/calendar/calendar-elements'
import CompanyDropdown from '../../../util/form/dropdown/CompanyDropdown/CompanyDropdown'
import {PhoneComponent} from '../../../util/form/PhoneComponent'

// describes the general data of a person
// to be used in form context
export const GeneralPersonData = (props: {
  person: Person | undefined
  userType: UserAccountType
  checkDuplicate: (entry: string) => boolean
}): JSX.Element => {
  const {
    control,
    formState: { errors },
  } = useFormContext()

  const salutations: SalutationElement[] = [
    {
      value: SalutationCt.NoSpecification,
      label: t<string>('core.no_specification'),
    },
    { value: SalutationCt.Ms, label: t<string>('salutation.ms') },
    { value: SalutationCt.Mr, label: t<string>('salutation.mr') },
    { value: SalutationCt.Diverse, label: t<string>('salutation.diverse') },
  ]

  return (
    <Fragment>
      <Fieldset legend={t<string>('hrm.general')} toggleable>
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-3 mb-2">
            <label
              htmlFor="salutation"
              className={classNames({
                'p-error': errors.salutation,
              })}
            >
              {t<string>('person.salutation')}
            </label>
            <Controller
              name="salutation"
              control={control}
              render={({ field, fieldState }) => (
                <Dropdown
                  value={field.value}
                  appendTo={document.body}
                  options={salutations}
                  onChange={(e) => {
                    field.onChange(e.value)
                  }}
                  className={classNames({ 'p-invalid': fieldState.error })}
                />
              )}
            />

            {getFormErrorMessage(errors, 'salutation')}
          </div>

          <div className="field col-12 md:col-3 mb-2">
            <label
              htmlFor="first_name"
              className={classNames({
                'p-error': errors.first_name,
              })}
            >
              {t<string>('person.first_name')}*
            </label>
            <Controller
              name="first_name"
              control={control}
              rules={{
                required: getRequiredFormMessage(
                  t<string>('person.first_name')
                ),
              }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  value={field.value || ''}
                  maxLength={50}
                  onChange={(e) => {
                    field.onChange(e)
                  }}
                  className={classNames({ 'p-invalid': fieldState.error })}
                />
              )}
            />
            {getFormErrorMessage(errors, 'first_name')}
          </div>

          <div className="field col-12 md:col-3 mb-2">
            <label
              htmlFor="middle_name"
              className={classNames({
                'p-error': errors.middle_name,
              })}
            >
              {t<string>('person.middle_name')}
            </label>
            <Controller
              name="middle_name"
              control={control}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  maxLength={50}
                  value={field.value || ''}
                  className={classNames({ 'p-invalid': fieldState.error })}
                />
              )}
            />
          </div>

          <div className="field col-12 md:col-3 mb-2">
            <label
              htmlFor="last_name"
              className={classNames({ 'p-error': errors.last_name })}
            >
              {t<string>('person.last_name')}*
            </label>
            <Controller
              name="last_name"
              control={control}
              rules={{
                required: getRequiredFormMessage(t<string>('person.last_name')),
              }}
              render={({ field, fieldState }) => (
                <InputText
                  id={field.name}
                  {...field}
                  maxLength={60}
                  className={classNames({ 'p-invalid': fieldState.error })}
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e)
                  }}
                />
              )}
            />
            {getFormErrorMessage(errors, 'last_name')}
          </div>
          <div className="field col-12 md:col-6 mb-2">
            <label
              htmlFor="email"
              className={classNames({ 'p-error': errors.email })}
            >
              {t<string>('person.email')}*
            </label>
            <span className="p-float-label p-input-icon-right">
              <i className="pi pi-envelope" />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: getRequiredFormMessage(t<string>('person.email')),
                  pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                  // check if email is duplicate
                  validate: (entry) => props.checkDuplicate(entry),
                }}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    {...field}
                    value={field.value || ''}
                    maxLength={160}
                    onChange={(e) => {
                      field.onChange(e)
                    }}
                    className={classNames({ 'p-invalid': fieldState.error })}
                  />
                )}
              />
            </span>
            {getEmailErrorMessage(errors)}
          </div>

          <div className="field col-12 md:col-3 mb-2">
            <label htmlFor="birthdate">{t<string>('person.birthdate')}</label>
            <span className="p-input-icon-right">
              <Controller
                name="birthdate"
                control={control}
                render={({ field }) => (
                  <Calendar
                    appendTo={document.body}
                    id={field.name}
                    {...field}
                    dateFormat={'dd.mm.yy'}
                    monthNavigator
                    value={field.value ? new Date(field.value) : undefined}
                    onChange={(e) => {
                      field.onChange(e.value)
                    }}
                    placeholder={dayjs(new Date()).format(
                      t<string>('locales.dateFormat')
                    )}
                    monthNavigatorTemplate={getMonthNavigatorTemplate}
                    yearNavigator
                    maxDate={new Date()}
                    yearRange={getBirthDayYearRange()}
                    yearNavigatorTemplate={getYearNavigatorTemplate}
                    showIcon
                    showButtonBar={true}
                  />
                )}
              />
            </span>
            {getFormErrorMessage(errors, 'birthdate')}
          </div>
          <div className={'col-12'} />
          <PostBox />

          <div className="field col-12 md:col-6">
            <label htmlFor={'id_company'}>{t<string>('person.company')}</label>
            <CompanyDropdown />
          </div>
          <div className="field col-12 md:col-6 mt-4 p-fluid">
            <PhoneComponent />
          </div>
        </div>
      </Fieldset>
    </Fragment>
  )
}